import { ErrorBoundary, withSentryRouting } from '@sentry/react';
import { usePermissions } from 'contexts/PermissionsContext';
import {
    corporationRoute,
    locationRoute,
    systemRoute,
    useRouteEntities,
} from 'contexts/RouteEntityContext';
import { CorporationLandingPage } from 'pages/Corporation/CorporationLandingPage';
import { DashboardLandingPage } from 'pages/Dashboard/DashboardLandingPage';
import { DeploymentAndValueDashboard } from 'pages/Dashboard/DeploymentAndValueDashboard';
import { DeskActivityOverviewDashboard } from 'pages/Dashboard/DeskActivityOverviewDashboard';
import { ValueByLocationDashboard } from 'pages/Dashboard/ValueByLocationDashboard/ValueByLocationDashboard';
import { ErrorPage, GlobalErrorPage } from 'pages/ErrorPage';
import { LocationLandingPage } from 'pages/Location/LocationLandingPage';
import { NotFound } from 'pages/NotFound';
import { SystemsOverviewPage } from 'pages/workbench/SystemsOverviewPage';
import { lazily } from 'react-lazily';
import { Redirect, RouteProps, Route as RRoute, Switch } from 'react-router-dom';
import {
    ProtectedCorporationContent,
    ProtectedLocationContent,
    ProtectedSystemContent,
} from 'routing/EntityContent';
import { LandingPage } from 'routing/LandingPage';
import { PremiumContent } from 'routing/PremiumContent';
import { ProtectedContent } from 'routing/ProtectedContent';
import { QuickLinkHandler, QuickLinkUrlPrefix } from 'routing/QuickLinkHandler';

const { ComponentDemoPage: DemoPage } = lazily(
    () => import('pages/Demo/ComponentDemoPage'),
);
const { MapPage } = lazily(() => import('pages/Map/MapPage'));
const { HardwarePage } = lazily(() => import('pages/Hardware/HardwarePage'));
const { SettingsPage } = lazily(() => import('pages/Settings/SettingsPage'));
const { DashboardPage } = lazily(() => import('pages/Dashboard/DashboardPage'));
const { ReportsPage } = lazily(() => import('pages/Reports/ReportsPage'));
const { SaaSLandingPage } = lazily(() => import('pages/SaaS/SaaSLandingPage'));
const { WorkbenchPage } = lazily(() => import('pages/workbench/WorkbenchPage'));
const { DeskPage } = lazily(() => import('pages/Desk/DeskPage'));

const SentryRoute = withSentryRouting(RRoute);

function Route(props: RouteProps): JSX.Element {
    return (
        <ErrorBoundary fallback={ErrorPage}>
            <SentryRoute {...props} />
        </ErrorBoundary>
    );
}

export type RouteParams = Readonly<{
    corporationId: string | undefined;
    locationId: string | undefined;
    dashboardId: string | undefined;
    systemId: string | undefined;
    tabId: string | undefined;
}>;

export function AppRoutes() {
    const {
        canViewDemo,
        canViewReports,
        canViewGlobalReports,
        canViewHardware,
        canViewSettings,
    } = usePermissions();

    const { corporation: currentCorporation } = useRouteEntities();
    const allowComponentPage =
        !window.location.hostname.includes('sd.kcftech.com') && canViewDemo;

    // NOTE: The login page does not appear in here, because it is handled by Amplify, which intercepts the request
    // and renders the login page instead. The login page handles routing inside of itself, so check there for any
    // login-related routes (login, reset password, etc.). Since this is the case, attempting to access the login page
    // here just redirects to the landing page.
    return (
        <Switch>
            <Route exact path="/" component={LandingPage} />
            <Route path="/login">
                <Redirect to="/" />
            </Route>
            <Route
                path="/sales"
                render={() => (
                    <ProtectedContent pageName="internal-landing">
                        <SaaSLandingPage />
                    </ProtectedContent>
                )}
                exact
            />
            <Route
                path="/sentry"
                render={() => (
                    <ProtectedContent pageName="sentry-landing">
                        <SaaSLandingPage />
                    </ProtectedContent>
                )}
                exact
            />
            <Route
                path="/internal"
                render={() => (
                    <ProtectedContent pageName="saas-landing">
                        <SaaSLandingPage />
                    </ProtectedContent>
                )}
                exact
            />
            <Route
                path={`${QuickLinkUrlPrefix}/*`}
                render={() => <QuickLinkHandler />}
                exact
            />
            {canViewReports && (
                <Route
                    path={`${corporationRoute}/reports`}
                    render={() => (
                        <ProtectedCorporationContent
                            pageName="corporation-reports"
                            render={({ corporation }) => (
                                <ReportsPage corporation={corporation} />
                            )}
                        />
                    )}
                />
            )}
            {canViewReports && (
                <Route
                    path={`${locationRoute}/reports`}
                    render={() => (
                        <ProtectedLocationContent
                            pageName="location-reports"
                            render={({ corporation, location }) => (
                                <ReportsPage
                                    corporation={corporation}
                                    location={location}
                                />
                            )}
                        />
                    )}
                />
            )}
            {canViewReports && canViewGlobalReports && (
                <Route
                    path="/reports"
                    render={() => (
                        <ProtectedContent pageName="global-reports">
                            <ReportsPage />
                        </ProtectedContent>
                    )}
                />
            )}
            {canViewHardware && (
                <Route
                    path={`${locationRoute}/hardware`}
                    render={() => (
                        <ProtectedLocationContent
                            pageName="location-hardware"
                            render={({ corporation, location }) => (
                                <HardwarePage
                                    corporation={corporation}
                                    location={location}
                                />
                            )}
                        />
                    )}
                />
            )}
            {canViewHardware && (
                <Route
                    path={`${corporationRoute}/hardware`}
                    render={() => (
                        <ProtectedCorporationContent
                            pageName="corporation-hardware"
                            render={({ corporation }) => (
                                <HardwarePage corporation={corporation} />
                            )}
                        />
                    )}
                />
            )}
            {canViewHardware && (
                <Route
                    path="/hardware"
                    render={() => (
                        <ProtectedContent pageName="global-hardware">
                            <HardwarePage />
                        </ProtectedContent>
                    )}
                />
            )}
            {/** START OF Premium Routes */}
            {canViewSettings && (
                <Route
                    path="/settings"
                    render={() => (
                        // only allow all KCF to access Settings if there is no corporation
                        <PremiumContent
                            allowSaas
                            allowKCF={currentCorporation === undefined}
                        >
                            <ProtectedContent pageName="global-settings">
                                <SettingsPage />
                            </ProtectedContent>
                        </PremiumContent>
                    )}
                />
            )}
            <Route
                path={corporationRoute}
                exact
                render={() => (
                    <PremiumContent>
                        <ProtectedCorporationContent
                            pageName="corporation-landing"
                            render={({ corporation }) => (
                                <CorporationLandingPage corporation={corporation} />
                            )}
                        />
                    </PremiumContent>
                )}
            />
            <Route
                path={locationRoute}
                exact
                render={() => (
                    <PremiumContent>
                        <ProtectedLocationContent
                            pageName="location-landing"
                            render={({ location }) => (
                                <LocationLandingPage location={location} />
                            )}
                        />
                    </PremiumContent>
                )}
            />
            <Route
                path={`${corporationRoute}/dashboards`}
                exact
                render={() => (
                    <PremiumContent>
                        <ProtectedCorporationContent
                            pageName="corporation-dashboard-landing"
                            render={({ corporation }) => (
                                <DashboardLandingPage
                                    corporation={corporation}
                                    key={corporation.Id}
                                />
                            )}
                        />
                    </PremiumContent>
                )}
            />
            <Route
                path={`${corporationRoute}/dashboards/deployment-and-value`}
                exact
                render={() => (
                    <PremiumContent>
                        <ProtectedCorporationContent
                            pageName="corporation-dashboard-deployment-and-value"
                            render={({ corporation }) => (
                                <DeploymentAndValueDashboard corporation={corporation} />
                            )}
                        />
                    </PremiumContent>
                )}
            />
            <Route
                path={`${corporationRoute}/dashboards/desk-activity-overview`}
                exact
                render={() => (
                    <PremiumContent>
                        <ProtectedCorporationContent
                            pageName="corporation-dashboard-desk-activity-overview"
                            render={({ corporation }) => (
                                <DeskActivityOverviewDashboard
                                    corporation={corporation}
                                />
                            )}
                        />
                    </PremiumContent>
                )}
            />
            <Route
                path={`${corporationRoute}/dashboards/value-by-location`}
                exact
                render={() => (
                    <PremiumContent>
                        <ProtectedCorporationContent
                            pageName="corporation-dashboard-value-by-location"
                            render={({ corporation }) => (
                                <ValueByLocationDashboard corporation={corporation} />
                            )}
                        />
                    </PremiumContent>
                )}
            />
            <Route
                path={`${corporationRoute}/dashboards/:dashboardId`}
                exact
                render={() => (
                    <PremiumContent>
                        <ProtectedCorporationContent
                            pageName="corporation-dashboard-single"
                            render={({ corporation }) => (
                                <DashboardPage corporation={corporation} />
                            )}
                        />
                    </PremiumContent>
                )}
            />
            <Route
                path={`${locationRoute}/dashboards`}
                exact
                render={() => (
                    <PremiumContent>
                        <ProtectedLocationContent
                            pageName="location-dashboard-landing"
                            render={({ corporation, location }) => (
                                <DashboardLandingPage
                                    corporation={corporation}
                                    location={location}
                                    key={location.Id}
                                />
                            )}
                        />
                    </PremiumContent>
                )}
            />
            <Route
                path={`${locationRoute}/dashboards/:dashboardId`}
                exact
                render={() => (
                    <PremiumContent>
                        <ProtectedLocationContent
                            pageName="location-dashboard-single"
                            render={({ corporation, location }) => (
                                <DashboardPage
                                    corporation={corporation}
                                    location={location}
                                />
                            )}
                        />
                    </PremiumContent>
                )}
            />
            <Route
                path={`${locationRoute}/workbench`}
                exact
                render={() => (
                    <PremiumContent>
                        <ProtectedLocationContent
                            pageName="systems-overview"
                            render={({ location }) => (
                                <SystemsOverviewPage location={location} />
                            )}
                        />
                    </PremiumContent>
                )}
            />
            <Route
                path={`${systemRoute}/workbench`}
                render={() => (
                    <PremiumContent>
                        <ProtectedSystemContent
                            pageName="workbench-page"
                            render={({ location, system }) => (
                                <WorkbenchPage location={location} system={system} />
                            )}
                        />
                    </PremiumContent>
                )}
            />
            <Route
                path={`${locationRoute}/desk`}
                exact={false}
                render={() => (
                    <PremiumContent>
                        <ProtectedLocationContent
                            pageName="desk-page"
                            render={({ location }) => <DeskPage location={location} />}
                        />
                    </PremiumContent>
                )}
            />
            <Route
                exact={false}
                path={`${corporationRoute}/maps/table`}
                render={() => (
                    <PremiumContent>
                        <ProtectedCorporationContent
                            pageName="corporation-map-table-page"
                            render={({ corporation }) => (
                                <MapPage corporation={corporation} isTableView />
                            )}
                        />
                    </PremiumContent>
                )}
            />
            <Route
                exact={false}
                path={`${locationRoute}/maps/table`}
                render={() => (
                    <PremiumContent>
                        <ProtectedLocationContent
                            pageName="location-map-table-page"
                            render={({ corporation, location }) => (
                                <MapPage
                                    corporation={corporation}
                                    location={location}
                                    isTableView
                                />
                            )}
                        />
                    </PremiumContent>
                )}
            />
            <Route
                exact={false}
                path={`${systemRoute}/maps/table`}
                render={() => (
                    <PremiumContent>
                        <ProtectedSystemContent
                            pageName="system-map-table-page"
                            render={({ corporation, location, system }) => (
                                <MapPage
                                    corporation={corporation}
                                    location={location}
                                    system={system}
                                    isTableView
                                />
                            )}
                        />
                    </PremiumContent>
                )}
            />
            <Route
                exact={false}
                path={`${corporationRoute}/maps/:mapId`}
                render={() => (
                    <PremiumContent>
                        <ProtectedCorporationContent
                            pageName="corporation-map-page"
                            render={({ corporation }) => (
                                <MapPage corporation={corporation} />
                            )}
                        />
                    </PremiumContent>
                )}
            />
            <Route
                exact={false}
                path={`${corporationRoute}/maps`}
                render={() => (
                    <PremiumContent>
                        <ProtectedCorporationContent
                            pageName="corporation-map-page"
                            render={({ corporation }) => (
                                <MapPage corporation={corporation} isTableView />
                            )}
                        />
                    </PremiumContent>
                )}
            />
            <Route
                exact={false}
                path={`${locationRoute}/maps/:mapId`}
                render={() => (
                    <PremiumContent>
                        <ProtectedLocationContent
                            pageName="location-map-page"
                            render={({ corporation, location }) => (
                                <MapPage corporation={corporation} location={location} />
                            )}
                        />
                    </PremiumContent>
                )}
            />
            <Route
                exact={false}
                path={`${locationRoute}/maps`}
                render={() => (
                    <PremiumContent>
                        <ProtectedLocationContent
                            pageName="location-map-page"
                            render={({ corporation, location }) => (
                                <MapPage
                                    corporation={corporation}
                                    location={location}
                                    isTableView
                                />
                            )}
                        />
                    </PremiumContent>
                )}
            />
            <Route
                exact={false}
                path={`${systemRoute}/maps/:mapId`}
                render={() => (
                    <PremiumContent>
                        <ProtectedSystemContent
                            pageName="system-map-page"
                            render={({ corporation, location, system }) => (
                                <MapPage
                                    corporation={corporation}
                                    location={location}
                                    system={system}
                                />
                            )}
                        />
                    </PremiumContent>
                )}
            />
            <Route
                exact={false}
                path={`${systemRoute}/maps`}
                render={() => (
                    <PremiumContent>
                        <ProtectedSystemContent
                            pageName="system-map-page"
                            render={({ corporation, location, system }) => (
                                <MapPage
                                    corporation={corporation}
                                    location={location}
                                    system={system}
                                    isTableView
                                />
                            )}
                        />
                    </PremiumContent>
                )}
            />
            {canViewSettings && (
                <Route
                    path={`${locationRoute}/settings`}
                    render={() => (
                        <PremiumContent allowSaas>
                            <ProtectedLocationContent
                                pageName="location-settings"
                                render={({ corporation, location }) => (
                                    <SettingsPage
                                        corporation={corporation}
                                        location={location}
                                    />
                                )}
                            />
                        </PremiumContent>
                    )}
                />
            )}
            {canViewSettings && (
                <Route
                    path={`${corporationRoute}/settings`}
                    render={() => (
                        <PremiumContent allowSaas>
                            <ProtectedCorporationContent
                                pageName="corporation-settings"
                                render={({ corporation }) => (
                                    <SettingsPage corporation={corporation} />
                                )}
                            />
                        </PremiumContent>
                    )}
                />
            )}
            {/** END OF Premium Routes */}
            {allowComponentPage && (
                <Route
                    path="/demo"
                    exact
                    render={() => (
                        <ProtectedContent pageName="components-demo">
                            <DemoPage />
                        </ProtectedContent>
                    )}
                />
            )}
            <Route
                path="/error"
                exact
                render={() => (
                    <ProtectedContent pageName="error">
                        <GlobalErrorPage />
                    </ProtectedContent>
                )}
            />
            <Route
                path="/*"
                exact
                render={() => (
                    <ProtectedContent pageName="error-not-found">
                        <NotFound />
                    </ProtectedContent>
                )}
            />
            <Route>
                <Redirect to="/" />
            </Route>
        </Switch>
    );
}
